import { FC, MouseEventHandler } from 'react';
import Image from 'next/image';
import { Article as TArticle } from 't2-graphql-types';
import cx from 'classnames';
import { TextInput } from 'flowbite-react';
import { ClipLoader } from 'react-spinners';
import { SearchIcon } from '../Icons/SearchIcon';
import { MAX_SEARCH_LENGTH } from '../SearchForm/constants';
import CloudinaryResponsiveImage from '../CloudinaryResponsiveImage/CloudinaryResponsiveImage';
import { getMultilineEllipsisStyle } from '../PromptCard/utils/getMultilineEllipsisStyle';
import Typo from '../Typography/Typo';
import { DEFAULT_ARTICLE_IMAGE } from '../ArticleCard/constants';
import { Placeholder } from '../Placeholder/Placeholder';
import Button from '../Button';
import { NotificationSuccessIcon } from '../NotificationSuccessIcon/NotificationSuccessIcon';

export const PromptLinkPostModalContent = ({
  onSelectArticle,
  onSearchChange,
  posts,
  search,
  isLoading,
  isCalled,
}: {
  onSelectArticle: (articleId: string) => void;
  onSearchChange: (search: string) => void;
  search: string;
  posts: (TArticle & { isChecked: boolean; isLoading: boolean })[];
  isLoading: boolean;
  isCalled: boolean;
}) => (
  <div className="relative flex flex-col gap-8">
    <TextInput
      id="link-post-search"
      color="t2-search"
      inputMode="search"
      icon={SearchIcon}
      onChange={(e) => onSearchChange(e.target.value)}
      value={search}
      type="search"
      placeholder="Search posts"
      className="invisile border-none focus:border-none w-full outline-none group-focus:visible"
      maxLength={MAX_SEARCH_LENGTH}
    />
    <div className="flex flex-col gap-y-1 overflow-y-auto sm:h-96">
      {!isLoading &&
        posts.map((article) => (
          <Post
            key={article.id}
            title={article.title!}
            shortDescription={article.shortDescription!}
            coverImage={article.coverImage}
            value={article.id}
            onClick={(e) => onSelectArticle(e.currentTarget.value)}
            isChecked={article.isChecked}
            isLoading={article.isLoading}
          />
        ))}
      {(isLoading || !isCalled) &&
        Array.from({ length: 6 })
          .map((_, i) => i)
          .map((i) => <PostLoader key={i} />)}
      {!isLoading && isCalled && posts.length === 0 && (
        <div className="flex justify-center text-p2-m text-t2-neutral-grey04">No posts eligible for linking</div>
      )}
    </div>
  </div>
);

const Post: FC<
  Pick<TArticle, 'title' | 'shortDescription' | 'coverImage'> & {
    value: string;
    onClick: MouseEventHandler<HTMLButtonElement>;
    isChecked: boolean;
    isLoading: boolean;
  }
> = ({ title, coverImage, value, onClick, isChecked, isLoading }) => (
  <div
    className={cx(
      'flex justify-between items-center py-2 px-4 h-16 rounded-[8px]  border',
      'bg-white-900 border-t2-neutral-grey03 gap-3',
      {
        'cursor-pointer': !isChecked,
        'border-t2-success-neonGreen pointer-events-none': isChecked,
      },
    )}
  >
    <div className="flex min-w-0 w-full gap-4 items-center">
      <div className="flex w-full gap-4 items-center">
        <div className="shrink-0 size-10 rounded-md overflow-hidden">
          {coverImage ? (
            <CloudinaryResponsiveImage className="size-10" image={coverImage} alt="Article cover" />
          ) : (
            <Image
              priority
              src={DEFAULT_ARTICLE_IMAGE}
              className="h-10 min-w-10 object-cover"
              width={120}
              height={120}
              alt="post image"
            />
          )}
        </div>
        <div>
          <Typo
            text={title}
            className={cx('text-start text-p2-m multiline-ellipsis')}
            style={getMultilineEllipsisStyle(1)}
          />
        </div>
      </div>
      <div className="w-28">
        <Button
          variant={!isLoading && isChecked ? 'success' : 'primary'}
          onClick={onClick}
          clickable={!isLoading}
          value={value}
          rounded
          fullWidth
        >
          <div className="w-full flex justify-center items-center gap-2">
            Link
            {isLoading && <ClipLoader size={24} color="#929292" />}
            {!isLoading && isChecked && <NotificationSuccessIcon color="white" />}
          </div>
        </Button>
      </div>
    </div>
  </div>
);

const PostLoader: FC<{}> = () => (
  <div
    className={cx(
      'flex justify-between items-center py-2 px-4 h-16 rounded-[8px] cursor-pointer border',
      'bg-white-900 border-t2-neutral-grey03 gap-3 hover:border-t2-neutral-grey05',
    )}
  >
    <div className="flex gap-4 items-center min-w-0 w-full">
      <div className="size-10 rounded-md shrink-0">
        <Placeholder className="size-10" />
      </div>
      <div className="flex w-full">
        <Placeholder className="h-6 w-3/4 rounded-md" />
      </div>
    </div>
  </div>
);
